import React, { useCallback } from "react";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import pxToRem from "~libs/pxToRem";
import { makeStyles } from "@mui/styles";
import { Tooltip } from "@mui/material";
import ItemLoadingCard from "./ItemLoadingCard";
import IconEdit from "~assets/icons/ic_edit.svg";
import useField from "~hooks/useField";
import WhatsappIcon from "~assets/icons/whatsapp.svg";
import type { FieldChangeEvent } from "./types";

interface ItemInputCardProps {
  id?: string;
  label?: string;
  value?: string | null;
  onChange?: (event: FieldChangeEvent) => void;
  placeholder?: string;
  required?: boolean;
  displayError?: boolean;
  focus?: boolean;
  onBlur?: () => void;
  readOnly?: boolean;
  isLoading?: boolean;
  maxLength?: number;
  multiline?: boolean;
  formatValue?: (value: string) => string;
  customValidate?: (value: string | null) => boolean;
  numbersOnly?: boolean;
  requiredText?: string;
  invalidText?: string;
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2%",
    borderBottom: "1px solid #8B8B8B",
  },
  caption: {
    fontFamily: "Nunito",
    fontSize: "16px",
    fontWeight: 600,
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: 1.38,
    letterSpacing: "normal",
    textAlign: "left",
    color: "#8B8B8B",
  },
  body: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    maxWidth: pxToRem(270),
    width: "50%",
  },
  value: {
    fontFamily: "Nunito",
    fontSize: "16px",
    fontWeight: "bold",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: 1.38,
    letterSpacing: "normal",
    textAlign: "right",
    color: "#424242",
  },
  input: {
    padding: "5%",
    textAlign: "end",
    width: "100%",
    "& .Mui-error": {
      borderRadius: "4px",
      border: "solid 1.7px #f77221",
    },
    "&:Mui-error": {
      borderRadius: "4px",
      border: "solid 1.7px #f77221",
    },
  },
  buttonLink: {
    display: "flex",
    alignItems: "center",
    color: "#f77221",
    paddingLeft: 8,
    cursor: "pointer",
  },
  icon: { color: "#6240a5" },
  helperText: {
    minHeight: 0,
    marginTop: 4,
    transition: "min-height .15s ease",
  },
  helperTextError: {
    minHeight: 18,
  },
  outlinedRoot: {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#8B8B8B33" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#793CFF",
      borderWidth: 2,
    },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: "#f55050",
      borderWidth: 2,
    },
  },
}));

const ItemInputCard: React.FC<ItemInputCardProps> = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  required,
  displayError,
  focus,
  onBlur,
  readOnly,
  isLoading,
  maxLength,
  multiline,
  formatValue,
  customValidate,
  numbersOnly,
  requiredText = "Requerido",
  invalidText,
}) => {
  const classes = useStyles();

  const validateFunction = useCallback(
    (value: string | null) => {
      const baseOk = !required || !!value;
      const extraOk =
        typeof customValidate === "function" ? customValidate(value) : true;
      return Boolean(baseOk && extraOk);
    },
    [required, customValidate]
  );

  const [localValue, setValue, handleEdit, reference, markTouched] = useField(
    value,
    validateFunction,
    focus,
    isLoading,
    readOnly
  );

  const handleBlur = () => {
    markTouched();
    if (typeof formatValue === "function") {
      const formatted = formatValue(localValue.value ?? "");
      if (formatted !== localValue.value) {
        setValue((prev) => ({ ...prev, value: formatted }));
        if (onChange) {
          onChange({
            value: formatted,
            valid: validateFunction(formatted),
            focus: false,
          });
        }
      }
    }
    if (onBlur) onBlur();
  };

  const handleChangeValue = (value: string) => {
    if (numbersOnly) value = value.replace(/\D/g, "");
    setValue((prev) => ({ ...prev, value: value }));

    if (onChange)
      onChange({
        value: value,
        valid: validateFunction(value),
        focus: false,
      });
  };

  const showError = !localValue.valid && (localValue.touched || displayError);

  let helperText = "";
  if (showError) {
    const val = localValue.value ?? "";
    const isEmptyRequired = required && !val;
    const failsCustom =
      typeof customValidate === "function" ? !customValidate(val) : false;

    if (isEmptyRequired) {
      helperText = requiredText;
    } else if (failsCustom) {
      helperText = invalidText ?? "";
    } else {
      helperText = requiredText;
    }
  }

  if (isLoading) return <ItemLoadingCard label={label} />;

  return (
    <div className={classes.container}>
      <Typography className={classes.caption}>{label}</Typography>
      <div className={classes.body}>
        {localValue.editing && (
          <TextField
            id={id}
            style={{ width: "100%" }}
            autoComplete="off"
            inputProps={{
              maxLength: maxLength,
              className: classes.input,
              autoComplete: "off",
              autoCorrect: "off",
              spellCheck: false,
            }}
            InputProps={{ classes: { root: classes.outlinedRoot } }}
            onChange={(event) => handleChangeValue(event.target.value)}
            onBlur={handleBlur}
            value={localValue.value}
            variant="outlined"
            placeholder={placeholder}
            error={showError}
            helperText={helperText}
            inputRef={reference}
            multiline={multiline}
          />
        )}

        {!localValue.editing && (
          <>
            {value && (
              <Typography className={classes.value}>{value}</Typography>
            )}
            {!value && <div>&mdash;</div>}
            {readOnly && renderWhatsappIcon(id, value)}
            {!readOnly && (
              <div
                className={classes.buttonLink}
                onClick={() => handleEdit(true)}
              >
                <img alt="" src={IconEdit} className={classes.icon} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ItemInputCard;

const renderWhatsappIcon = (
  id: string | undefined,
  value: string | null | undefined
): React.ReactNode => {
  const telefonoWhatsappFormat = `54${value
    ?.toString()
    .replace(/^0|15/, "")
    .replace(/\s/g, "")}`;

  if (id === "telefono") {
    return (
      <Tooltip title="Chatear por Whatsapp">
        <a
          href={"https://wa.me/" + telefonoWhatsappFormat}
          rel="noopener noreferrer"
          target="_blank"
          data-tool="true"
          style={{ marginLeft: "10px", display: "flex", alignItems: "center" }}
        >
          <img
            alt=""
            src={WhatsappIcon}
            style={{ width: "1.3em", color: "#4aba7c" }}
          />
        </a>
      </Tooltip>
    );
  }
  return null;
};

// Dead code — kept for reference. The component uses an inline validateFunction above.
// const handleValidate = (required: boolean) => (value: string | null) => {
//   const isDataValid = !required || value ? true : false;
//   return isDataValid;
// };
